import _ from 'lodash';
import { BASE_TOOL_COMMON_PROPS } from '@/toolSelection/baseTool.constants';
import { BaseToolStore } from '@/toolSelection/baseTool.store';
import { TREND_TOOLS } from '@/toolSelection/investigate.constants';
import { OutputType } from '@/tools/onnx/onnx.constants';
import type { BaseToolPersistedState, ToolItem } from '@/toolSelection/baseTool.store';
import type { OnnxResultType } from '@/tools/onnx/onnx.constants';
import type { OnnxInputParameter } from '@/tools/onnx/onnx.utilities';

export const DEFAULT_DURATION = { value: 0, units: 'min' };

interface DurationItem {
  value: number;
  units: string;
}

interface OnnxState extends BaseToolPersistedState {
  color: string;
  formulaItemId: string | null;
  onnxResultType: OnnxResultType | null;
  onnxInputParameters: OnnxInputParameter[];
  outputType: OutputType;
  outputSignal: string | null;
  cleanCapsules: boolean;
  capsuleMinDuration: DurationItem;
  capsuleMergeDuration: DurationItem;
  restrictToCondition?: ToolItem | null;
}

export type OnnxDehydratedState = OnnxState;

export class OnnxStore extends BaseToolStore {
  static readonly storeName = 'onnxStore';
  type = TREND_TOOLS.ONNX;
  parameterDefinitions = {
    restrictToCondition: {
      predicate: ['name', 'restrictToCondition'],
    },
  };

  initialize(): void {
    const initialState: OnnxState = {
      ...BASE_TOOL_COMMON_PROPS,
      color: '',
      formulaItemId: null,
      onnxResultType: null,
      onnxInputParameters: [],
      outputType: OutputType.CONDITION,
      outputSignal: null,
      cleanCapsules: false,
      capsuleMinDuration: DEFAULT_DURATION,
      capsuleMergeDuration: DEFAULT_DURATION,
      restrictToCondition: undefined,
    };
    this.state = this.immutable(initialState);
  }

  get formulaItemId(): string | null {
    return this.state.get('formulaItemId');
  }

  get onnxResultType(): OnnxResultType | null {
    return this.state.get('onnxResultType');
  }

  get onnxInputParameters(): OnnxInputParameter[] {
    return this.state.get('onnxInputParameters');
  }

  get restrictToCondition(): ToolItem | undefined {
    return this.state.get('restrictToCondition');
  }

  get color(): string {
    return this.state.get('color');
  }

  get outputType(): OutputType {
    return this.state.get('outputType');
  }

  get outputSignal(): string | null {
    return this.state.get('outputSignal');
  }

  get cleanCapsules(): boolean {
    return this.state.get('cleanCapsules');
  }

  get capsuleMinDuration(): DurationItem {
    return this.state.get('capsuleMinDuration');
  }

  get capsuleMergeDuration(): DurationItem {
    return this.state.get('capsuleMergeDuration');
  }

  dehydrate(): OnnxDehydratedState {
    return this.state.serialize();
  }

  rehydrate(dehydratedState: Partial<OnnxDehydratedState>): void {
    this.state.merge(dehydratedState);
  }

  setColor(payload: { color: string }): void {
    this.state.set('color', payload.color);
  }

  setOutputType(payload: { outputType: OutputType }): void {
    this.state.set('outputType', payload.outputType);
  }

  setFormulaItemId(payload: { formulaItemId: string }): void {
    this.state.set('formulaItemId', payload.formulaItemId);
  }

  setOnnxResultType(payload: { onnxResultType: OnnxResultType }): void {
    this.state.set('onnxResultType', payload.onnxResultType);
  }

  setOnnxInputParameters(payload: { parameters: OnnxInputParameter[] }): void {
    this.state.set('onnxInputParameters', payload.parameters);
  }

  setCleanCapsules(payload: { cleanCapsules: boolean }): void {
    this.state.set('cleanCapsules', payload.cleanCapsules);
  }

  setCapsuleMinDuration(payload: { capsuleMinDuration: DurationItem }): void {
    this.state.set('capsuleMinDuration', payload.capsuleMinDuration);
  }

  setCapsuleMergeDuration(payload: { capsuleMergeDuration: DurationItem }): void {
    this.state.set('capsuleMergeDuration', payload.capsuleMergeDuration);
  }

  updateOnnxInputParameter(payload: {
    parameter: { identifier: string; name: string; item: { id: string; name: string } };
  }): void {
    const index = _.findIndex(this.onnxInputParameters, { identifier: payload.parameter.identifier });
    if (index === -1) {
      throw Error(`Onnx input parameter with identifier ${payload.parameter.identifier} not found.`);
    }
    this.state.set(['onnxInputParameters', index], payload.parameter);
  }

  setOutputSignal(payload: { outputSignal: string }): void {
    this.state.set('outputSignal', payload.outputSignal);
  }

  protected readonly handlers = {
    ...this.baseHandlers,
    ONNX_SET_COLOR: this.setColor,
    ONNX_SET_OUTPUT_TYPE: this.setOutputType,
    ONNX_SET_CLEAN_CAPSULES: this.setCleanCapsules,
    ONNX_SET_CAPSULE_MIN_DURATION: this.setCapsuleMinDuration,
    ONNX_SET_CAPSULE_MERGE_DURATION: this.setCapsuleMergeDuration,
    ONNX_SET_FORMULA_ITEM_ID: this.setFormulaItemId,
    ONNX_SET_ONNX_RESULT_TYPE: this.setOnnxResultType,
    ONNX_SET_INPUT_PARAMETERS: this.setOnnxInputParameters,
    ONNX_UPDATE_INPUT_PARAMETER: this.updateOnnxInputParameter,
    ONNX_SET_OUTPUT_SIGNAL: this.setOutputSignal,
  };
}
